import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

import { useReleaseFlows, useDeleteReleaseFlow } from '../hooks/useReleaseFlow';
import { useRoles } from '../auth/useRoles';
import type { ReleaseFlowDto } from '../types/releaseFlow';

export const ReleaseFlowListPage: React.FC = () => {
  const navigate = useNavigate();
  const { canWrite } = useRoles();
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, error } = useReleaseFlows({
    nameFilter: searchTerm,
    isActive: showActiveOnly ? true : undefined,
    page,
    pageSize,
  });

  const deleteMutation = useDeleteReleaseFlow();

  const handleDelete = async (releaseFlow: ReleaseFlowDto) => {
    if (!window.confirm(`Are you sure you want to delete "${releaseFlow.name}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(releaseFlow.id);
      toast.success('Release flow deleted successfully');
    } catch (error) {
      console.error('Error deleting release flow:', error);
      toast.error('Failed to delete release flow');
    }
  };

  const handleEdit = (releaseFlow: ReleaseFlowDto) => {
    navigate(`/release-flows/${releaseFlow.id}/edit`);
  };

  const handleView = (releaseFlow: ReleaseFlowDto) => {
    navigate(`/release-flows/${releaseFlow.id}`);
  };

  const handleCreateNew = () => {
    navigate('/release-flows/new');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Release Flows</h1>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
        </div>
      </div>
    );
  }

  const releaseFlows = data?.data || [];
  const totalCount = data?.totalCount || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Release Flows</h1>
              <p className="text-gray-600 mt-2">
                Manage your deployment workflows and release processes
              </p>
            </div>
            {canWrite && (
              <button
                onClick={handleCreateNew}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                <span>New Release Flow</span>
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search release flows..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Active filter */}
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Active only</span>
                </label>
                <div className="text-sm text-gray-500">
                  {totalCount} flow{totalCount !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Release Flows Grid */}
        {releaseFlows.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="mx-auto max-w-sm">
              <div className="h-24 w-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <PlusIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No release flows found</h3>
              <p className="text-gray-600 mb-6">
                Get started by creating your first release flow to manage your deployment processes.
              </p>
              <button
                onClick={handleCreateNew}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Create Release Flow
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {releaseFlows.map((releaseFlow) => (
              <div
                key={releaseFlow.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {releaseFlow.name}
                      </h3>
                      {releaseFlow.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {releaseFlow.description}
                        </p>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      {releaseFlow.isActive ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <ExclamationCircleIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                    <div>{releaseFlow.totalNodes} nodes</div>
                    <div>{releaseFlow.totalEdges} edges</div>
                    {releaseFlow.hasCycles && (
                      <div className="text-red-600 font-medium">Has cycles</div>
                    )}
                  </div>

                  {/* Validation errors */}
                  {releaseFlow.validationErrors.length > 0 && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800">
                            Validation Issues
                          </h4>
                          <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                            {releaseFlow.validationErrors.slice(0, 2).map((error, index) => (
                              <li key={index} className="truncate">{error}</li>
                            ))}
                            {releaseFlow.validationErrors.length > 2 && (
                              <li>+{releaseFlow.validationErrors.length - 2} more</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Updated date */}
                  <div className="text-xs text-gray-500 mb-4">
                    Updated {new Date(releaseFlow.updatedAt || releaseFlow.createdAt).toLocaleDateString()}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleView(releaseFlow)}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>View</span>
                    </button>
                    
                    <div className="flex items-center space-x-3">
                      {canWrite && (
                        <button
                          onClick={() => handleEdit(releaseFlow)}
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                      )}
                      {canWrite && (
                        <button
                          onClick={() => handleDelete(releaseFlow)}
                          disabled={deleteMutation.isPending}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                        >
                          <TrashIcon className="h-4 w-4" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {data && data.totalCount > pageSize && (
          <div className="mt-8 flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-600">
                Page {page} of {Math.ceil(data.totalCount / pageSize)}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={!data.hasNextPage}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};